"use client";

import { useState, useEffect, useRef, useCallback } from "react";
import { Send, Shield, Star, Users } from "lucide-react";

type Message = {
  id: string;
  text: string;
  createdAt: string;
  user: {
    id: string;
    username: string;
    avatarUrl: string | null;
    role: string;
    isVerified: boolean;
    isPremium: boolean;
  };
};

const ROLE_BADGES: Record<string, { label: string; color: string }> = {
  ADMIN: { label: "ADMIN", color: "bg-red-600" },
  CREATOR: { label: "INMATE", color: "bg-pink-600" },
};

export default function ChatRoomPage() {
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [onlineCount, setOnlineCount] = useState(0);
  const scrollRef = useRef<HTMLDivElement>(null);
  const lastMsgRef = useRef<string | null>(null);

  const fetchMessages = useCallback(async () => {
    try {
      const url = lastMsgRef.current ? `/api/chat?after=${lastMsgRef.current}` : `/api/chat`;
      const res = await fetch(url);
      if (!res.ok) return;
      const data: Message[] = await res.json();
      if (data.length > 0) {
        lastMsgRef.current = data[data.length - 1].id;
        setMessages((prev) => {
          const ids = new Set(prev.map((m) => m.id));
          return [...prev, ...data.filter((m) => !ids.has(m.id))].slice(-300);
        });
      }
    } catch {}
  }, []);

  useEffect(() => {
    fetchMessages();
    const interval = setInterval(fetchMessages, 2500);
    return () => clearInterval(interval);
  }, [fetchMessages]);

  useEffect(() => {
    if (scrollRef.current) {
      const el = scrollRef.current;
      const near = el.scrollHeight - el.scrollTop - el.clientHeight < 120;
      if (near) el.scrollTop = el.scrollHeight;
    }
  }, [messages]);

  // Fake online count based on message activity
  useEffect(() => {
    const unique = new Set(messages.slice(-50).map((m) => m.user.id));
    setOnlineCount(Math.max(unique.size, 1));
  }, [messages]);

  const handleSend = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim() || sending) return;
    setSending(true);
    try {
      const res = await fetch("/api/chat", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text: input.trim() }),
      });
      if (res.ok) {
        const msg = await res.json();
        setMessages((prev) => [...prev, msg]);
        lastMsgRef.current = msg.id;
        setInput("");
      } else {
        const err = await res.json().catch(() => ({}));
        if (err.error) alert(err.error);
      }
    } catch {}
    setSending(false);
  };

  const getRoleColor = (role: string, isPremium: boolean) => {
    if (role === "ADMIN") return "text-red-500";
    if (role === "CREATOR") return "text-pink-400";
    if (isPremium) return "text-yellow-400";
    return "text-neutral-300";
  };

  return (
    <main className="min-h-screen bg-[#050505] text-white flex flex-col">

      {/* HEADER BAR */}
      <div className="bg-[#0a0a0a] border-b border-white/[0.04] px-4 py-3 flex items-center justify-between shrink-0">
        <div>
          <h1 className="text-lg font-black uppercase tracking-wider" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
            The Rec Room
          </h1>
          <p className="text-[10px] text-neutral-500 uppercase tracking-widest">Global Chat</p>
        </div>
        <div className="flex items-center gap-2 text-[11px] text-neutral-500">
          <div className="flex items-center gap-1.5">
            <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
            <span className="font-bold">{onlineCount}</span>
          </div>
          <Users className="w-4 h-4 text-neutral-600" />
        </div>
      </div>

      {/* MESSAGES */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-3 space-y-1.5 scrollbar-hide">
        {messages.length === 0 && (
          <div className="text-center py-20">
            <div className="text-neutral-700 text-sm font-mono uppercase tracking-widest mb-2">The Rec Room is empty</div>
            <p className="text-neutral-600 text-xs">Be the first to drop a kite</p>
          </div>
        )}
        {messages.map((msg) => {
          const roleBadge = ROLE_BADGES[msg.user.role];
          return (
            <div key={msg.id} className="flex gap-2.5 py-1 hover:bg-white/[0.015] rounded-lg px-2 -mx-2 transition">
              {msg.user.avatarUrl ? (
                <img src={msg.user.avatarUrl} alt="" className="w-8 h-8 rounded-full object-cover border border-neutral-800 shrink-0 mt-0.5" />
              ) : (
                <div className="w-8 h-8 rounded-full bg-neutral-800 flex items-center justify-center text-[10px] font-bold text-neutral-500 shrink-0 mt-0.5">
                  {msg.user.username[0].toUpperCase()}
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5 mb-0.5">
                  <span className={`text-[12px] font-bold ${getRoleColor(msg.user.role, msg.user.isPremium)}`}>
                    {msg.user.username}
                  </span>
                  {roleBadge && (
                    <span className={`text-[7px] font-black text-white px-1.5 py-0.5 rounded ${roleBadge.color}`}>
                      {roleBadge.label}
                    </span>
                  )}
                  {msg.user.isVerified && <Shield className="w-3 h-3 text-blue-500" />}
                  {msg.user.isPremium && msg.user.role === "CONSUMER" && <Star className="w-3 h-3 text-yellow-500" />}
                  <span className="text-[9px] text-neutral-700 ml-auto font-mono">
                    {new Date(msg.createdAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                  </span>
                </div>
                <p className="text-[13px] text-neutral-300 break-words leading-relaxed">{msg.text}</p>
              </div>
            </div>
          );
        })}
      </div>

      {/* INPUT */}
      <div className="px-3 py-2.5 bg-[#0a0a0a] border-t border-white/[0.04] shrink-0">
        <form onSubmit={handleSend} className="flex items-center gap-2">
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Drop a kite in the rec room..."
            maxLength={500}
            className="flex-1 h-10 bg-white/[0.04] border border-white/[0.06] rounded-full pl-4 pr-4 text-[13px] text-white placeholder-neutral-600 focus:border-red-700/50 focus:outline-none transition"
          />
          <button
            type="submit"
            disabled={sending || !input.trim()}
            className="h-10 w-10 flex items-center justify-center rounded-full bg-red-600 hover:bg-red-500 text-white transition disabled:opacity-30 shrink-0"
          >
            <Send className="w-4 h-4" />
          </button>
        </form>
      </div>
    </main>
  );
}
